import React from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import * as S from "../../constants/StringConstants";
import Icon, { IconName } from "src/components/Icon/Icon";
import { MessageStatuses } from "./CampaignDetail";

interface StepChartProps {
  messageQueues: { status: number; error?: string }[];
  width?: number;
  height?: number;
  outerRadius?: number;
  baseThickness?: number;
  taperStep?: number;
  gapAngle?: number;
}

function polarToCartesian(cx: number, cy: number, r: number, angle: number) {
  const rad = (angle * Math.PI) / 180;
  return {
    x: cx + r * Math.cos(rad),
    y: cy + r * Math.sin(rad),
  };
}

function describeArc(cx: number, cy: number, r: number, startAngle: number, endAngle: number) {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArcFlag = Math.abs(endAngle - startAngle) > 180 ? "1" : "0";
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
}

export const StepCharts: React.FC<StepChartProps> = ({
  messageQueues,
  width = 216,
  height = 216,
  outerRadius = 100,
  baseThickness = 30,
  taperStep = 5,
  gapAngle = 25,
}) => {
  const statusOrder = [1, 2, 4, 6];

  const colorMap: Record<number, string> = {
    1: "var(--app-color-chart-purple)",
    2: "var(--app-color-chart-blue)",
    4: "var(--app-color-chart-pink)",
    6: "var(--app-color-chart-blue2)",
  };

  const iconMap: Record<number, IconName> = {
    1: IconName.CampaignsToSendStatus,
    2: IconName.CampaignsSentStatus,
    4: IconName.CampaignsErrorStatus,
    6: IconName.CampaignsPausedStatus,
  };

  const data = statusOrder
    .map(statusId => {
      const count = messageQueues.filter(m => m.status === statusId).length;
      return {
        value: count,
        label: MessageStatuses[statusId],
        color: colorMap[statusId],
        statusId,
      };
    })
    .filter(d => d.value > 0);

  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return null;

  const sentObj = data.find(d => d.statusId === 2);
  const sentCount = sentObj?.value ?? 0;
  const sentPercent = Math.round((sentCount / total) * 100);
  const startAngle = gapAngle / 2;
  const endAngle = 360 - gapAngle / 2;
  const angleRange = endAngle - startAngle;

  let currentAngle = startAngle;

  const cx = width / 2;
  const cy = height / 2;
  const arcs = data.map(d => {
    const sliceAngle = (d.value / total) * angleRange;
    const sliceStart = currentAngle;
    const sliceEnd = currentAngle + sliceAngle;
    currentAngle = sliceEnd;
    const orderIndex = statusOrder.indexOf(d.statusId);
    const thickness = baseThickness - orderIndex * taperStep;
    const innerR = outerRadius - thickness;

    return {
      color: d.color,
      label: d.label,
      count: d.value,
      sliceStart,
      sliceEnd,
      innerR,
    };
  });

  return (
    <Box display="flex" alignItems="center" gap="100px">
      <Box
        position="relative"
        width={width}
        height={height}
        mt="-10px"
        ml="200px" >
        <svg width={width} height={height}>
          {[...arcs].reverse().map((arc, i) => {
            const isStartSlice = Math.abs(arc.sliceStart - startAngle) < 0.0001;
            const isEndSlice = Math.abs(arc.sliceEnd - endAngle) < 0.0001;

            const tooltipText = `${arc.label}: ${arc.count}`;

            if (isStartSlice || isEndSlice) {
              const r = (arc.innerR + outerRadius) / 2;
              const strokeWidth = outerRadius - arc.innerR;

              return (
                <Tooltip key={i} title={<span style={{ fontSize: "14px" }}>{tooltipText}</span>} arrow>
                  <path
                    d={describeArc(cx, cy, r, arc.sliceStart - 90, arc.sliceEnd - 90)}
                    fill="none"
                    stroke={arc.color}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round" />
                </Tooltip>
              );
            }

            const x1 = cx + outerRadius * Math.cos((arc.sliceStart - 90) * Math.PI / 180);
            const y1 = cy + outerRadius * Math.sin((arc.sliceStart - 90) * Math.PI / 180);
            const x2 = cx + outerRadius * Math.cos((arc.sliceEnd - 90) * Math.PI / 180);
            const y2 = cy + outerRadius * Math.sin((arc.sliceEnd - 90) * Math.PI / 180);
            const x3 = cx + arc.innerR * Math.cos((arc.sliceEnd - 90) * Math.PI / 180);
            const y3 = cy + arc.innerR * Math.sin((arc.sliceEnd - 90) * Math.PI / 180);
            const x4 = cx + arc.innerR * Math.cos((arc.sliceStart - 90) * Math.PI / 180);
            const y4 = cy + arc.innerR * Math.sin((arc.sliceStart - 90) * Math.PI / 180);
            const largeArc = (arc.sliceEnd - arc.sliceStart) > 180 ? 1 : 0;

            const path = `
              M ${x1} ${y1}
              A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${x2} ${y2}
              L ${x3} ${y3}
              A ${arc.innerR} ${arc.innerR} 0 ${largeArc} 0 ${x4} ${y4}
              Z
            `;

            return (
              <Tooltip key={i} title={<span style={{ fontSize: "14px", fontFamily: "Roboto" }}>{tooltipText}</span>} arrow>
                <path d={path} fill={arc.color} style={{ cursor: "pointer" }} />
              </Tooltip>
            );
          })}
        </svg>

        <Box
          position="absolute"
          top="50%"
          left="50%"
          sx={{
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            pointerEvents: "none",
          }} >
          <Typography sx={{ fontSize: "30px", fontWeight: 800 }}>{sentPercent}%</Typography>
          <Typography sx={{ fontSize: "16px", fontWeight: 400 }}>{S.CAMP_PERFORMANCE_SENT}</Typography>
        </Box>
      </Box>

      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        gap={2} >
        {data.map(d => (
          <Box key={d.label} display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: "5px",
                backgroundColor: d.color,
              }} />
            <Typography sx={{ fontSize: "16px", fontWeight: 600 }}>{d.label}</Typography>
          </Box>
        ))}
      </Box>

      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        gap={2}
        minWidth={220}
        ml="auto"
        mr="50px" >
        {data.map(d => {
          if (d.value <= 0) return null;
          const isOpened = d.statusId === 1;
          const displayValue = isOpened
            ? `${((d.value / total) * 100).toFixed(2)}%`
            : d.value;

          return (
            <Box
              key={d.label}
              sx={{
                width: 373,
                height: 90,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                p: 2,
                borderRadius: 3,
                bgcolor: "var(--app-color-secondary7)",
                gap: 2,
              }} >

              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  bgcolor: "var(--app-color-secondary8)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  pointerEvents: "none",
                }} >
                <Icon name={iconMap[d.statusId]} size="28px" />
              </Box>

              <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-start" >
                <Typography sx={{ fontSize: "16px", lineHeight: 1.3, fontWeight: 600, color: "var(--app-color-neutral26)" }}>{d.label.toUpperCase()}</Typography>
                <Typography sx={{ fontSize: "24px", lineHeight: 1.3, fontWeight: 600 }}>{displayValue}</Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
